import { useState } from 'react';
import Image from 'next/image';
import { A11y, Autoplay, Mousewheel } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, Card, Flex, Heading, Text, useBreakpointValue } from '@chakra-ui/react';

import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/autoplay';

export type TestimonialCarouselProps = {
  testimonials: Array<{
    labelKey: string;
    quoteKey: string;
    title: string;
    name: string;
    imagePath: string;
    quoteColor?: string;
    labelColor?: string;
  }>;
  initialSlide?: number;
  /** When false, Swiper does not auto-advance. Defaults to true. */
  autoplay?: boolean;
  /** Disables looping so the row stays static (and centered) whenever every card fits. */
  scrollOnlyOnOverflow?: boolean;
  onInteraction?: () => void;
};

export function TestimonialCarousel({
  testimonials,
  autoplay = true,
  initialSlide = 3,
  scrollOnlyOnOverflow = false,
  onInteraction,
}: TestimonialCarouselProps) {
  const { t, i18n } = useI18n();
  const clientReady = useClientRender();
  const [readyForMobile, setReadyForMobile] = useState<boolean | undefined>(undefined);
  const cardSize = useBreakpointValue(
    {
      base: {
        width: '302px',
        height: '480px',
      },
      md: {
        width: '340px',
        height: '540px',
      },
    },
    { fallback: 'md' }
  );
  // Default `md` matches every other carousel caller. `scrollOnlyOnOverflow` ties
  // centering to `isMobile`, so prefer `base` and take the remount on desktop.
  const isMobile = useBreakpointValue(
    {
      base: true,
      md: false,
    },
    { fallback: scrollOnlyOnOverflow ? 'base' : 'md' }
  );

  // Swiper only locks a non-looping row that fits when slides are not centered.
  const centeredSlides = !scrollOnlyOnOverflow || Boolean(isMobile);
  const centeredInitialSlide = centeredSlides ? Math.floor(testimonials.length / 2) : 0;
  const resolvedInitialSlide = scrollOnlyOnOverflow ? centeredInitialSlide : initialSlide;
  const edgeGutter = scrollOnlyOnOverflow ? (isMobile ? 16 : 24) : 0;
  const isPositioned = !scrollOnlyOnOverflow || (clientReady && readyForMobile === isMobile);

  return (
    <Box margin='0 auto' maxWidth='1728px' position='relative' width='100%'>
      <Box
        isolation='isolate'
        minHeight={scrollOnlyOnOverflow ? cardSize?.height : undefined}
        opacity={isPositioned ? 1 : 0}
        pointerEvents={isPositioned ? 'auto' : 'none'}
        visibility={isPositioned ? 'visible' : 'hidden'}
        sx={{
          '.swiper .swiper-slide': {
            height: 'auto',
            display: 'flex',
          },
        }}
      >
        <Swiper
          key={isMobile ? 'mobile' : 'desktop'}
          centerInsufficientSlides={scrollOnlyOnOverflow}
          centeredSlides={centeredSlides}
          centeredSlidesBounds={scrollOnlyOnOverflow}
          direction='horizontal'
          initialSlide={resolvedInitialSlide}
          longSwipes={false}
          longSwipesMs={115}
          loop={!scrollOnlyOnOverflow}
          loopedSlides={!isMobile && !scrollOnlyOnOverflow ? 2 : undefined}
          modules={[A11y, Mousewheel, Autoplay]}
          slidesOffsetAfter={edgeGutter}
          slidesOffsetBefore={edgeGutter}
          slidesPerView='auto'
          spaceBetween={24}
          autoplay={
            autoplay
              ? {
                  delay: 4000,
                  disableOnInteraction: true,
                  pauseOnMouseEnter: true,
                  stopOnLastSlide: false,
                  waitForTransition: true,
                }
              : false
          }
          mousewheel={{
            forceToAxis: true,
            releaseOnEdges: true,
            thresholdDelta: 10,
          }}
          onAutoplayStop={() => {
            // Autoplay stops on first user interaction when it is enabled.
            onInteraction?.();
          }}
          onBeforeInit={(swiper) => {
            swiper.changeLanguageDirection(i18n.dir());
          }}
          onInit={() => {
            setReadyForMobile(isMobile);
          }}
          onScroll={() => {
            if (!autoplay) {
              onInteraction?.();
            }
          }}
          onSliderFirstMove={() => {
            if (!autoplay) {
              onInteraction?.();
            }
          }}
        >
          {testimonials.map((testimonial) => (
            <SwiperSlide
              key={testimonial.name}
              style={{
                width: cardSize?.width,
                height: cardSize?.height,
              }}
            >
              <Card
                border='1px solid'
                borderColor='hsla(0, 0%, 100%, 1)'
                boxShadow='none'
                overflow='hidden'
                position='relative'
                width='100%'
              >
                <Image
                  alt=''
                  loading='lazy'
                  sizes='340px'
                  src={testimonial.imagePath}
                  style={{ objectFit: 'cover', pointerEvents: 'none' }}
                  fill
                />
                <Flex
                  flexDirection='column'
                  inset={0}
                  justifyContent='space-between'
                  position='absolute'
                  padding={{
                    base: '22px',
                    md: '28px 30px',
                  }}
                >
                  <Flex
                    alignItems='center'
                    gap={4}
                    _after={{
                      content: "''",
                      display: 'block',
                      width: '100%',
                      height: '0.5px',
                      backgroundColor:
                        testimonial.labelColor === 'light' ? 'hsla(255, 100%, 100%, 0.66)' : 'hsla(0, 0%, 0%, 0.66)',
                      flex: 1,
                    }}
                  >
                    <Heading
                      as='p'
                      fontFamily='condensed'
                      fontSize='0.9375rem'
                      fontWeight='500'
                      lineHeight='0.9375'
                      textTransform='uppercase'
                      color={
                        testimonial.labelColor === 'light' ? 'hsla(255, 100%, 100%, 0.66)' : 'hsla(0, 0%, 0%, 0.66)'
                      }
                    >
                      {t(testimonial.labelKey)}
                    </Heading>
                  </Flex>
                  <Flex
                    flexDirection='column'
                    gap={{
                      base: 4,
                      md: 5,
                    }}
                  >
                    <Box
                      as='blockquote'
                      color={testimonial.quoteColor === 'light' ? 'white' : 'black'}
                      lineHeight='1.4'
                      fontSize={{
                        base: '0.875rem',
                        md: '0.9375rem',
                      }}
                    >
                      &ldquo;{t(testimonial.quoteKey)}&rdquo;
                    </Box>
                    <Flex>
                      <Box as='footer' color={testimonial.quoteColor === 'light' ? 'white' : 'black'}>
                        <Heading as='p' fontSize='1.125rem' fontWeight='600' lineHeight='1.1' mb={1}>
                          {testimonial.name}
                        </Heading>
                        <Text
                          color={testimonial.quoteColor === 'light' ? 'white' : 'black'}
                          fontSize='0.8125rem'
                          lineHeight='1.1'
                          opacity={0.66}
                        >
                          {testimonial.title}
                        </Text>
                      </Box>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
      <Box
        bgGradient='linear(to left, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 1) 100%)'
        bottom={0}
        height='100%'
        left={0}
        pointerEvents='none'
        position='absolute'
        top={0}
        width='300px'
        display={{
          base: 'none',
          '2xl': 'block',
        }}
      />
      <Box
        bgGradient='linear(to right, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 1) 100%)'
        bottom={0}
        height='100%'
        pointerEvents='none'
        position='absolute'
        right={0}
        top={0}
        width='300px'
        display={{
          base: 'none',
          '2xl': 'block',
        }}
      />
    </Box>
  );
}
